import React, { useMemo, useRef, useState, useEffect } from "react";
import { Button, Col, Flex, Image, Row, Space, Typography, message } from "antd";
import Uploader from "./uploader/Uploader";
import ThemeSelector from "./controls/ThemeSelector";
import GenderSelector from "./controls/GenderSelector";
import LoaderOverlay from "./feedback/LoaderOverlay";
import ResultCard from "./result/ResultCard";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import {
  getRandomThemeImageBase64,
  blobToBase64,
} from "../services/storageService";
import { runpodGenerate } from "../services/runpodClient";
import { initializeFirebase } from "../firebase";
import "../App.css";
import { LuSettings2, LuImage, LuSparkles, LuCamera, LuUser, LuUser2 } from "react-icons/lu";
import { UserOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const THEMES = [
  { key: "carmen", label: "Carmen" },
  { key: "don-pasquale", label: "Don Pasquale" },
  { key: "naksitrallid", label: "Naksitrallid" },
  { key: "pelleas", label: "Pelléas ja Mélisande" },
  { key: "volufloot", label: "Võluflööt" },
];

const GENDERS = [
  { key: "mees", label: "Mees" },
  { key: "naine", label: "Naine" },
];

export default function GeneratorPage({
  onShowResult,
  isResultVisible,
  resultData,
  onBackToGenerator,
}) {
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0].key);
  const [gender, setGender] = useState(GENDERS[0].key);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [status, setStatus] = useState("IN_QUEUE");
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize Firebase on component mount
  useEffect(() => {
    initializeFirebase().catch((error) => {
      console.error("Failed to initialize Firebase:", error);
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const previewUrl = useMemo(() => {
    if (filePreview) return filePreview;
    // Static demo image placeholder
    return "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=800&auto=format&fit=crop";
  }, [filePreview]);

  const startLoader = async () => {
    if (!file && !filePreview) {
      message.warning("Palun laadi üles foto");
      return;
    }

    setLoading(true);
    setSeconds(0);
    setStatus("PROCESSING");

    // Create abort controller for request cancellation
    abortControllerRef.current = new AbortController();

    // Start count-up timer
    intervalRef.current = setInterval(() => {
      setSeconds((s) => +(s + 0.1).toFixed(1));
    }, 100);

    try {
      // Convert user image to base64 (strip data URL prefix)
      let personB64;
      if (file) {
        const base64Result = await blobToBase64(file);
        personB64 = String(base64Result).split(",")[1] || base64Result;
        console.log("User image base64 length:", personB64?.length);
      } else if (filePreview.startsWith("data:")) {
        personB64 = String(filePreview).split(",")[1];
        console.log(
          "User image from preview base64 length:",
          personB64?.length
        );
      } else {
        throw new Error(
          "Invalid user image format. Please upload a valid image file."
        );
      }

      // Validate base64 data
      if (
        !personB64 ||
        typeof personB64 !== "string" ||
        personB64.length < 100
      ) {
        throw new Error("Invalid user image data");
      }

      // Pick random base image from storage according to theme/gender
      console.log(
        `🎯 Fetching base image for theme: ${selectedTheme}, gender: ${gender}`
      );
      const baseB64 = await getRandomThemeImageBase64(selectedTheme, gender);
      if (!baseB64 || baseB64.length < 100) {
        throw new Error("Failed to fetch base image from Firebase Storage");
      }

      // Validate base image data
      if (!baseB64 || typeof baseB64 !== "string" || baseB64.length < 100) {
        throw new Error("Failed to fetch base image from Firebase Storage");
      }

      console.log(
        `✅ Base image fetched successfully: ${baseB64.length} characters`
      );

      // Call Runpod API
      const result = await runpodGenerate({
        baseImageB64: baseB64,
        personImageB64: personB64,
        apiKey: process.env.REACT_APP_RUNPOD_API_KEY
      });

      // Handle the new RunPod response format
      let outputSrc = null;
      console.log("API result:", result);
      
      if (result && result.hasImage && result.base64) {
        // New format: image is in result.base64
        outputSrc = `data:image/png;base64,${result.base64}`;
        console.log("✅ Using generated image from RunPod");
      } else {
        // No image generated - show appropriate message
        console.log("❌ No image generated by RunPod");
        throw new Error("No image was generated. Please try again with a different photo or settings.");
      }

      // Validate the final output
      if (!outputSrc || outputSrc.includes("[object Object]")) {
        console.error("Invalid output image data:", outputSrc);
        throw new Error("Invalid image data received from API");
      }

      clearInterval(intervalRef.current);
      setLoading(false);
      onShowResult({ image: outputSrc, theme: selectedTheme, gender });
    } catch (err) {
      console.error("Generation error:", err);
      clearInterval(intervalRef.current);
      setLoading(false);

      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Show specific error messages
      if (err.message.includes("Invalid user image data")) {
        message.error("Vale pildi formaat. Palun laadi üles kehtiv pilt.");
      } else if (err.message.includes("No image was generated")) {
        message.error("Pilti ei genereeritud. Palun proovi teist pilti või seadeid.");
      } else if (err.message.includes("Invalid image data received from API")) {
        message.error("API tagastas vigase pildi. Palun proovi uuesti.");
      } else if (err.message.includes("timed out")) {
        message.error(
          "Pildi genereerimine võttis liiga kaua aega. Palun proovi uuesti."
        );
      } else {
        message.error(`Pildi genereerimisel tekkis viga: ${err.message}`);
      }
    }
  };

  const onFileChange = (e) => {
    const fileObj = e.target.files && e.target.files[0];
    if (!fileObj) return;
    setFile(fileObj);
    const reader = new FileReader();
    reader.onload = () => {
      setFilePreview(String(reader.result));
    };
    reader.readAsDataURL(fileObj);
    message.success("Foto lisatud");
  };

  const removeImage = () => {
    setFile(null);
    setFilePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (isResultVisible && resultData) {
    return (
      <div className="container">
        <ResultCard
          image={resultData.image}
          downloadLabel="Laadi alla"
          regenerateLabel="Genereeri uuesti"
          onDownload={async () => {
            try {
              // Detect iOS devices
              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
              const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
              
              if (isIOS || isSafari) {
                // For iOS/Safari: Use native share functionality
                try {
                  // Convert base64 to blob
                  const response = await fetch(resultData.image);
                  const blob = await response.blob();
                  
                  // Check if Web Share API is supported
                  if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], 'ai-generated-image.jpg', { type: 'image/jpeg' })] })) {
                    // Use native share with file
                    const file = new File([blob], 'ai-generated-image.jpg', { type: 'image/jpeg' });
                    
                    await navigator.share({
                      title: 'AI Generated Image',
                      text: 'Vaadake minu AI-ga genereeritud pilti!',
                      files: [file]
                    });
                    
                    message.success("Pilt jagatud edukalt!");
                  } else if (navigator.share) {
                    // Use native share without file (fallback)
                    await navigator.share({
                      title: 'AI Generated Image',
                      text: 'Vaadake minu AI-ga genereeritud pilti! Vaadake seda siin:',
                      url: resultData.image
                    });
                    
                    message.success("Link jagatud edukalt!");
                  } else {
                    // Fallback: Show image in modal for manual save
                    const modal = document.createElement("div");
                    modal.style.cssText = `
                      position: fixed;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 100%;
                      background: rgba(0,0,0,0.9);
                      z-index: 10000;
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      justify-content: center;
                      padding: 20px;
                      box-sizing: border-box;
                    `;
                    
                    modal.innerHTML = `
                      <img src="${resultData.image}" style="max-width: 90%; max-height: 70%; object-fit: contain; border-radius: 8px;" />
                      <div style="color: white; text-align: center; margin-top: 20px; max-width: 300px;">
                        <p style="font-size: 16px; margin-bottom: 10px;">Kasutage pika vajutust pildil</p>
                        <p style="font-size: 14px; margin-bottom: 15px;">Valige "Salvesta pilt" või "Lisa fotod"</p>
                        <button onclick="this.parentElement.parentElement.remove()" style="
                          background: #007AFF;
                          color: white;
                          border: none;
                          padding: 10px 20px;
                          border-radius: 8px;
                          font-size: 16px;
                          cursor: pointer;
                        ">Sulge</button>
                      </div>
                    `;
                    
                    document.body.appendChild(modal);
                    
                    // Auto-close after 30 seconds
                    setTimeout(() => {
                      if (modal.parentElement) {
                        modal.remove();
                      }
                    }, 30000);
                  }
                  
                } catch (error) {
                  if (error.name === 'AbortError') {
                    // User cancelled the share
                    message.info("Jagamine tühistatud");
                  } else {
                    console.error('Share error:', error);
                    message.error("Viga pildi jagamisel");
                  }
                }
              } else if ('showSaveFilePicker' in window) {
                // Modern browsers with File System Access API
                try {
                  const response = await fetch(resultData.image);
                  const blob = await response.blob();
                  
                  const fileHandle = await window.showSaveFilePicker({
                    suggestedName: 'generated-image.jpg',
                    types: [
                      {
                        description: 'JPEG images',
                        accept: {
                          'image/jpeg': ['.jpg', '.jpeg'],
                        },
                      },
                      {
                        description: 'PNG images',
                        accept: {
                          'image/png': ['.png'],
                        },
                      },
                    ],
                  });
                  
                  const writable = await fileHandle.createWritable();
                  await writable.write(blob);
                  await writable.close();
                  
                  message.success("Pilt salvestatud valitud asukohta!");
                } catch (error) {
                  if (error.name !== 'AbortError') {
                    console.error('Error saving file:', error);
                    message.error("Viga faili salvestamisel");
                  }
                }
              } else {
                // Fallback for other browsers
            const link = document.createElement("a");
            link.href = resultData.image;
                link.download = "generated-image.jpg";
            link.click();
                message.success("Pilt laaditakse alla!");
              }
            } catch (error) {
              console.error('Download error:', error);
              message.error("Viga pildi allalaadimisel");
            }
          }}
          onRegenerate={onBackToGenerator}
        />
      </div>
    );
  }

  return (
    <div className="container">
        <img src="/assets/images/logo.png" style={{ width: '200px', marginBottom: '-20px', height: 'auto' }} alt="AI pildigeneraator" />
      <span className="title">AI pildigeneraator</span>
      <span className="subtitle" style={{ fontSize: '20px' }}>Vali ooper, lae üles enda foto ja vaata, kuidas näeksid sina välja Estonia teatri laval.<br />
#WorldOperaDay</span>
      <div className="panel">
        <div className="generator-container">
          {/* Generator Header */}
          <div className="generator-header">
           <LuSettings2 className="generator-icon" />
            <h2 className="generator-title">Generaator</h2>
          </div>
            <span className="section-title">Vali ooper</span>
            <div className="theme-grid">
              {THEMES.map((theme) => (
                <div
                  key={theme.key}
                  className={`theme-card ${selectedTheme === theme.key ? 'selected' : ''}`}
                  onClick={() => setSelectedTheme(theme.key)}
                >
                  <p className="theme-label">{theme.label}</p>
                </div>
              ))}
            </div>

          {/* Gender Section */}
          <div>
            <div className="gender-grid">
              {GENDERS.map((genderOption) => (
                <div
                  key={genderOption.key}
                  className={`theme-card ${gender === genderOption.key ? 'selected' : ''}`}
                  onClick={() => setGender(genderOption.key)}
                >
                  {genderOption.key === 'mees' ? <UserOutlined className="gender-icon" /> : <UserOutlined className="gender-icon" />}
                  <p className="theme-label">{genderOption.key === 'mees' ? 'Mees' : 'Naine'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Photo Section */}
          <div>
            <span className="section-title" style={{ marginBottom: '16px' }}>Foto</span>
            <Uploader
              hasFile={!!filePreview}
              previewUrl={filePreview}
              onChange={onFileChange}
              onRemove={removeImage}
              uploadTitle="Puuduta, et üles laadida või teha foto"
              uploadSub="JPG/PNG - kuni ~6 MB"
              removeLabel="Eemalda pilt"
              replaceLabel="Asenda pilt"
            />
          </div>

          {/* Generate Button */}
          <button className="generate-button" onClick={startLoader}>
            <LuSparkles className="generate-icon" />
            Loo pilt
          </button>
        </div>
      </div>

      {/* Privacy Policy Link */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setShowPrivacyModal(true);
          }}
          style={{ 
            color: 'white', 
            textDecoration: 'underline',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Privaatsuspoliitika
        </a>
      </div>

      {loading && (
        <LoaderOverlay
          title="Pildi genereerimine"
          timeLabel="Aeg"
          seconds={seconds}
          status={status}
        />
      )}

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </div>
  );
}
